import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, AppThunk } from '../../app/store';
import { fetchTimeTable } from './TimeTableAPI';
import { TicketItem } from '../ticket/ticketSlice';

export const getTestData = (d: Date) => {
	const yAgoD = new Date(d.getTime());
	yAgoD.setFullYear(d.getFullYear() - 1);
	const yAgo = yAgoD.toISOString();
	const MMMAgoD = new Date(d.getTime());
	MMMAgoD.setFullYear(d.getFullYear() - 1);
	const MMMAgo = MMMAgoD.toISOString();
	const MAgoD = new Date(d.getTime());
	MAgoD.setFullYear(d.getFullYear() - 1);
	const MAgo = MAgoD.toISOString();
	const testRows = [
		{
			item: {
				metadata: {
					id: "id-area-finance",
					old_id: null,
					user: {id: "1", name: "thge"},
					admin: null,
					organization: null,
					team: null,
					priv: true,
					created: {id: "1", name: "thge", ts: "2024-09-15T12:15:50Z"},
					modified: null,
					archived: null,
					deleted: null,
				},
				time: {
					start: null,
					end: null,
					planned_start: null,
					planned_end: null,
					deadline: null,
				},
				parent_id: "1",
				context_id: "1",
				area_id: "1",
				space_id: "1",
				ktype: '3',
				star: false,
				title: "Finances",
			}
		},
		{
			item: {
				metadata: {
					id: "id-space-job",
					old_id: null,
					user: {id: "1", name: "thge"},
					admin: null,
					organization: null,
					team: null,
					priv: true,
					created: {id: "1", name: "thge", ts: "2024-09-15T12:15:50Z"},
					modified: null,
					archived: null,
					deleted: null,
				},
				time: {
					start: null,
					end: null,
					planned_start: d.toISOString(),
					planned_end: null,
					deadline: null,
				},
				parent_id: "1",
				context_id: "1",
				area_id: "1",
				space_id: "1",
				ktype: '6',
				star: true,
				title: "My Job",
			}
		}
	];
}

export interface Data {
  id: string;
  ktype: string | null;
  star: boolean;
  start: string | null;
  end: string | null;
  title: string;
  context: string | null;
  parent: string | null;
}


function createData(
  id: string,
  ktype: string | null,
  star: boolean,
  start: string | null,
  end: string | null,
  title: string,
  context: string | null,
  parent: string | null,
): Data {
  return {
    id,
	ktype,
	star,
    start,
    end,
    title,
    context,
    parent,
  };
}

const testData = getTestData(new Date());

export const getRowsData = () => {
	const rows = [
		createData('1', '1', true, '2024-09-17T12:50:40Z', '305', '3.7', '67', '4.3'),
		createData('2', '1', false, '2024-09-17T18:50:42Z', '452', '25.0', '51', '4.9'),
		createData('3', '1', false, 'Eclair', '262', '16.0', '24', '6.0'),
		createData('4', '1', false, 'Frozen yoghurt', '159', '6.0', '24', '4.0'),
		createData('5', '1', false, 'Gingerbread', '356', '16.0', '49', '3.9'),
		createData('6', '1', false, 'Honeycomb', '408', '3.2', '87', '6.5'),
		createData('7', '1', false, 'Ice cream sandwich', '237', '9.0', '37', '4.3'),
		createData('8', '1', false, 'Jelly Bean', '375', '0.0', '94', '0.0'),
		createData('9', '1', false, 'KitKat', '518', '26.0', '65', '7.0'),
		createData('10', '1', false, 'Lollipop', '392', '0.2', '98', '0.0'),
		createData('11', '1', false, 'Marshmallow', '318', '0', '81', '2.0'),
		createData('12', '1', false, 'Nougat', '360', '19.0', '9', '37.0'),
		createData('13', '1', false, 'Oreo', '437', '18.0', '63', '4.0'),
	];
	return rows;
}

export interface TimeRow {
	item: TicketItem,
}

export const toTimeRow = (ti: TicketItem) => {
	const id = (ti.metadata.id) ? ti.metadata.id : '??';
	return createData(id, ti.ktype, ti.star, ti.time.planned_start, ti.time.end, ti.title, ti.context_id, ti.parent_id);
};

//export const toTimeRows = (data: {[key:string]: TicketItem}) => {
export const toTimeRows = (data: TicketItem[]) => {
	let dataList: Data[] = [];
	for (const key in data) {
		const ti = data[key];
		dataList.push(toTimeRow(ti));
	}
	return dataList;
};

export interface TimeTableState {
  value: number;
  status: 'idle' | 'loading' | 'failed';
}

const initialState: TimeTableState = {
  value: 0,
  status: 'idle',
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const incrementAsync = createAsyncThunk(
  'timeTable/fetchCount',
  async (amount: number) => {
    const response = await fetchTimeTable(amount);
    // The value we return becomes the `fulfilled` action payload
    return response.data;
  }
);

export const timeTableSlice = createSlice({
  name: 'timeTable',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    increment: (state) => {
      // Redux Toolkit allows us to write "mutating" logic in reducers. It
      // doesn't actually mutate the state because it uses the Immer library,
      // which detects changes to a "draft state" and produces a brand new
      // immutable state based off those changes
      state.value += 1;
    },
    decrement: (state) => {
      state.value -= 1;
    },
    // Use the PayloadAction type to declare the contents of `action.payload`
    incrementByAmount: (state, action: PayloadAction<number>) => {
      state.value += action.payload;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(incrementAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(incrementAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.value += action.payload;
      })
      .addCase(incrementAsync.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const { increment, decrement, incrementByAmount } = timeTableSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.timeTable.value)`
export const selectCount = (state: RootState) => state.timeTable.value;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const incrementIfOdd =
  (amount: number): AppThunk =>
  (dispatch, getState) => {
    const currentValue = selectCount(getState());
    if (currentValue % 2 === 1) {
      dispatch(incrementByAmount(amount));
    }
  };

export default timeTableSlice.reducer;
